/**
 * Reseller cart.
 *
 * In demo mode this is a thin layer over the demo store's reservation system.
 * In live mode each action calls the backend: add → POST /reservations,
 * remove → DELETE /reservations/:id, checkout → POST /orders. The cart tracks
 * the held reservations either way; live reservations are refetched from the
 * server so the wholesaler/admin views and other resellers see the same holds.
 */

import { create } from "zustand";
import { useDemoStore, availableQty } from "./store";
import { isDemoMode } from "./demo-mode";
import { orderApi } from "./api";
import { queryClient } from "./query-client";
import type { Order, Reservation } from "@/types/domain";

interface CartState {
  /** Reservations held by this reseller (records, not just ids). */
  items: Reservation[];
  /** Reserve a unit. Returns the reservation, or null if unavailable. */
  add: (
    product: { id: string; sizes: { size: string; stockId?: string }[] },
    size: string,
    qty: number,
    resellerId: string,
  ) => Promise<Reservation | null>;
  remove: (reservationId: string) => Promise<void>;
  /** Refresh the held cart from the server (live mode). */
  refresh: () => Promise<void>;
  clear: () => Promise<void>;
  checkoutAll: () => Promise<Order[]>;
}

export const useCart = create<CartState>((set, get) => ({
  items: [],

  add: async (product, size, qty, resellerId) => {
    if (isDemoMode()) {
      const res = useDemoStore.getState().reserve(product.id, size, qty, resellerId);
      if (res) set({ items: [...get().items, res] });
      return res;
    }
    const stockId = product.sizes.find((s) => s.size === size)?.stockId;
    if (!stockId) return null;
    try {
      const res = await orderApi.reserve(stockId, qty);
      // Re-pull the hydrated cart so every item has product/size/price details
      // (the POST response is a bare reservation row).
      await get().refresh();
      void queryClient.invalidateQueries();
      return res;
    } catch {
      return null;
    }
  },

  remove: async (reservationId) => {
    if (isDemoMode()) {
      useDemoStore.getState().releaseReservation(reservationId);
      set({ items: get().items.filter((r) => r.id !== reservationId) });
      return;
    }
    try {
      await orderApi.releaseReservation(reservationId);
    } finally {
      set({ items: get().items.filter((r) => r.id !== reservationId) });
      void queryClient.invalidateQueries();
    }
  },

  refresh: async () => {
    if (isDemoMode()) return;
    try {
      const items = await orderApi.cart();
      set({ items });
    } catch {
      /* keep existing */
    }
  },

  clear: async () => {
    if (isDemoMode()) {
      const store = useDemoStore.getState();
      get().items.forEach((r) => store.releaseReservation(r.id));
      set({ items: [] });
      return;
    }
    await Promise.all(get().items.map((r) => orderApi.releaseReservation(r.id).catch(() => {})));
    set({ items: [] });
    void queryClient.invalidateQueries();
  },

  checkoutAll: async () => {
    if (isDemoMode()) {
      const store = useDemoStore.getState();
      const orders: Order[] = [];
      get().items.forEach((r) => {
        const o = store.checkout(r.id);
        if (o) orders.push(o);
      });
      set({ items: [] });
      return orders;
    }
    try {
      const orders = await orderApi.checkout();
      set({ items: [] });
      void queryClient.invalidateQueries();
      return orders;
    } catch {
      return [];
    }
  },
}));

/** The live reservation records for the items in the cart. */
export function useCartItems(): Reservation[] {
  const items = useCart((s) => s.items);
  const reservations = useDemoStore((s) => s.reservations);
  if (isDemoMode()) {
    // Reflect the demo store's authoritative status.
    return items
      .map((r) => reservations.find((x) => x.id === r.id))
      .filter((r): r is Reservation => !!r && r.status === "held");
  }
  return items.filter((r) => r.status === "held");
}

export { availableQty };
